import { EMPTY_LIST, EMPTY_STRING } from '../../../../constants/common'
import { EMAIL_REPLY_FORM_HTML_BLOCK_ID } from '../../../../constants/email'
import { toHTML } from '../../../../utils/html'
import { getSignatureSelector } from '../../../../utils/signature'

import {
  LINKED_FILES_BLOCK_FILE_ICON_BASE64,
  LINKED_FILES_BLOCK_IDS_ATTRIBUTE,
  LINKED_FILES_BLOCK_QUERY,
  LINKED_FILES_BLOCK_SELECTOR,
  LINKED_FILES_ITEM_ID_ATTRIBUTE,
  LINKED_FILES_ITEM_QUERY,
} from './useEditorFileLinksBlock.constants'

function normalizeLinkedFiles(linkedFiles = EMPTY_LIST) {
  return linkedFiles
    .map(file => {
      const id = file?.id
      const title = file?.name || file?.filename

      if (Number.isFinite(id) && title) {
        return {
          id,
          title: toHTML(title),
        }
      }
    })
    .filter(Boolean)
}

function getLinkedFilesIconHtml() {
  return `
    <span
      style="
        display: inline-block;
        width: 32px;
        height: 32px;
        border-radius: 10px;
        background: #eaedf7;
        text-align: center;
        line-height: 32px;
      "
    >
      <img
        src="${LINKED_FILES_BLOCK_FILE_ICON_BASE64}"
        width="22"
        height="22"
        style="display: inline-block; vertical-align: middle;"
      />
    </span>
  `
}

function getFirstBodyChildByCursorY({
  cursorClientY,
  draggedLinksBlockElement,
  editorBodyElement,
}) {
  const children = Array.from(editorBodyElement.children).filter(
    child => child !== draggedLinksBlockElement
  )

  for (const child of children) {
    const { height, top } = child.getBoundingClientRect()

    if (cursorClientY <= top + height / 2) {
      return child
    }
  }

  return null
}

function getBodyChildIndex(bodyChildren, element) {
  return bodyChildren.findIndex(
    child => child === element || child.contains(element)
  )
}

export function getLinkedFilesInsertAnchorElement(body) {
  const replyElement = body.querySelector(`#${EMAIL_REPLY_FORM_HTML_BLOCK_ID}`)
  const signatureElement = body.querySelector(getSignatureSelector())

  const signatureOutsideReply =
    signatureElement && !replyElement?.contains(signatureElement)
      ? signatureElement
      : null

  if (!replyElement) {
    return signatureOutsideReply
  }

  if (!signatureOutsideReply) {
    return replyElement
  }

  const bodyChildren = Array.from(body.children)
  const replyIndex = getBodyChildIndex(bodyChildren, replyElement)
  const signatureIndex = getBodyChildIndex(bodyChildren, signatureOutsideReply)

  return replyIndex < signatureIndex ? replyElement : signatureOutsideReply
}

export function getLinksBlockHtml({
  infoText = EMPTY_STRING,
  linkedFiles = EMPTY_LIST,
}) {
  const normalizedLinkedFiles = normalizeLinkedFiles(linkedFiles)
  const linkedFilesIconHtml = getLinkedFilesIconHtml()

  const ids = normalizedLinkedFiles.map(file => file.id).join(',')

  const rows = normalizedLinkedFiles
    .map(
      ({ id, title }) => `
        <li
          style="
            box-sizing: border-box;
            width: 100%;
            padding: 8px 10px;
            margin-bottom: 8px;
            border: 1px solid #eaedf7;
            border-radius: 8px;
            max-width: 420px;
            min-width: 0;
            cursor: pointer;
            list-style-type: none;
          "
        >
          <a
            ${LINKED_FILES_ITEM_ID_ATTRIBUTE}="${id}"
            href=""
            target="_blank"
            rel="noreferrer"
            style="
              display: block;
              width: 100%;
              color: #1d4ed8;
              text-decoration: none;
              white-space: nowrap;
            "
          >
            <span style="display: inline-block; vertical-align: middle;">
              ${linkedFilesIconHtml}
            </span>
            <span
              style="
                display: inline-block;
                vertical-align: middle;
                margin-left: 10px;
                color: #1d4ed8;
                text-decoration: underline;
                white-space: nowrap;
              "
            >${title}</span>
          </a>
        </li>
      `
    )
    .join(EMPTY_STRING)

  return `
    <div
      ${LINKED_FILES_BLOCK_SELECTOR}
      ${LINKED_FILES_BLOCK_IDS_ATTRIBUTE}="${ids}"
      contenteditable="false"
      style="
        margin-top: 8px;
        padding: 10px;
        color: #242f57;
      "
    >
      <p>
        ${infoText}
      </p>
      <ul style="padding-left: 0; margin: 8px 0 0; list-style: none;">
        ${rows}
      </ul>
    </div>
  `
}

export function extractAttributeFromHtml(html, attr) {
  const match = html.match(new RegExp(`${attr}="([^"]*)"`))

  return match?.[1] ?? EMPTY_STRING
}

export function findLinkedFilesBlockElement(node) {
  const element =
    node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement

  return element?.closest?.(LINKED_FILES_BLOCK_QUERY) ?? null
}

export function findLinkedFilesItemElement(node) {
  const element =
    node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement

  return element?.closest?.(LINKED_FILES_ITEM_QUERY) ?? null
}

export function moveLinkedFilesBlock({
  cursorClientY,
  draggedLinksBlockElement,
  editorBodyElement,
  targetLinksBlockElement,
}) {
  if (!draggedLinksBlockElement || !editorBodyElement) {
    return false
  }

  if (
    targetLinksBlockElement &&
    targetLinksBlockElement !== draggedLinksBlockElement
  ) {
    const { height, top } = targetLinksBlockElement.getBoundingClientRect()
    const insertBefore = cursorClientY <= top + height / 2

    if (insertBefore) {
      targetLinksBlockElement.parentNode?.insertBefore(
        draggedLinksBlockElement,
        targetLinksBlockElement
      )
    } else {
      targetLinksBlockElement.parentNode?.insertBefore(
        draggedLinksBlockElement,
        targetLinksBlockElement.nextSibling
      )
    }

    return true
  }

  const nextBodyChild = getFirstBodyChildByCursorY({
    cursorClientY,
    draggedLinksBlockElement,
    editorBodyElement,
  })

  if (nextBodyChild) {
    editorBodyElement.insertBefore(draggedLinksBlockElement, nextBodyChild)

    return true
  }

  editorBodyElement.appendChild(draggedLinksBlockElement)

  return true
}

export function resetLinkedFilesBlockDragState({
  draggedBlockRef,
  handleSyncRef,
  isDraggingRef,
  pendingSyncRef,
}) {
  isDraggingRef.current = false
  draggedBlockRef.current = null

  if (pendingSyncRef.current) {
    pendingSyncRef.current = false
    handleSyncRef.current?.()
  }
}
